const TRANSITION_KEY = "worldTransition";

export const STAGES = {
  "world-map": { defaultSpawn: null, entrances: {}, exits: {} },
  castle: {
    defaultSpawn: [64, 512],
    entrances: { forest: [832, 576] },
    exits: { EAST: { cells: [[14, 8], [14, 9], [14, 10]], destination: "forest" } },
  },
  forest: {
    defaultSpawn: [384, 256],
    entrances: { castle: [64, 512], cliff: [832, 512], village: [384, 832] },
    exits: {
      WEST: { cells: [[0, 7]], destination: "castle" },
      EAST: { cells: [[14, 8]], destination: "cliff" },
      SOUTH: { cells: [[6, 14]], destination: "village" },
    },
  },
  cliff: {
    defaultSpawn: [512, 448],
    entrances: { forest: [64, 448] },
    exits: { WEST: { cells: [[0, 7]], destination: "forest" } },
  },
  village: {
    defaultSpawn: [384, 448],
    entrances: { forest: [384, 64], "lake-bed": [832, 448] },
    exits: {
      NORTH: { cells: [[6, 0]], destination: "forest" },
      EAST: { cells: [[14, 7]], destination: "lake-bed" },
    },
  },
  "lake-bed": {
    defaultSpawn: [64, 448],
    entrances: { village: [64, 448], "dragon-cave": [576, 64] },
    exits: {
      WEST: { cells: [[0, 7]], destination: "village" },
      NORTH: { cells: [[9, 0]], destination: "dragon-cave" },
    },
  },
  "dragon-cave": {
    defaultSpawn: [448, 832],
    entrances: { "lake-bed": [448, 832] },
    exits: { SOUTH: { cells: [[7, 14]], destination: "lake-bed" } },
  },
};

const copySpawn = (spawn) => (Array.isArray(spawn) ? spawn.slice() : null);
const isCell = (cell) => Array.isArray(cell) && cell.length === 2 &&
  cell.every((value) => Number.isInteger(value) && value >= 0 && value < 15);
const isSpawn = (spawn) => Array.isArray(spawn) && spawn.length === 2 &&
  spawn.every((value) => Number.isInteger(value) && value >= 0 && value <= 896 && value % 64 === 0);

export function getDefaultSpawn(stage) {
  const spawn = STAGES[stage] && STAGES[stage].defaultSpawn;
  return isSpawn(spawn) ? copySpawn(spawn) : null;
}

export function getEntranceSpawn(stage, fromStage) {
  const spawn = STAGES[stage] && STAGES[stage].entrances[fromStage];
  return isSpawn(spawn) ? copySpawn(spawn) : null;
}

export function getExitTransition(stage, direction, cell) {
  const exits = STAGES[stage] && STAGES[stage].exits;
  const exit = exits && exits[direction];
  if (!exit || !isCell(cell)) return null;
  return exit.cells.some(([column, row]) => column === cell[0] && row === cell[1])
    ? { destination: exit.destination, spawn: getEntranceSpawn(exit.destination, stage) }
    : null;
}

function storageKey(playerId) {
  return `${TRANSITION_KEY}:${playerId}`;
}

export function prepareStageEntry(playerId, stage, spawn) {
  if (!playerId || !STAGES[stage] || !isSpawn(spawn)) return false;
  sessionStorage.setItem(storageKey(playerId), JSON.stringify({ stage, spawn }));
  return true;
}

export function consumeStageEntry(playerId, stage) {
  if (!playerId) return null;
  const key = storageKey(playerId);
  let pending;
  try {
    pending = JSON.parse(sessionStorage.getItem(key));
  } catch (error) {
    pending = null;
  }
  if (!pending || !isSpawn(pending.spawn)) {
    sessionStorage.removeItem(key);
    return null;
  }
  if (pending.stage !== stage) return null;
  sessionStorage.removeItem(key);
  return copySpawn(pending.spawn);
}

export function getStageEntrySpawn(playerId, stage, fallbackSpawn) {
  return consumeStageEntry(playerId, stage) || getDefaultSpawn(stage) || copySpawn(fallbackSpawn);
}

// Resolve the explicit caller-owned spawn before storage/default state. World
// components pass this through a useState initializer so this resolution is
// performed once for the mounted stage.
export function resolveStageEntrySpawn(playerId, stage, explicitSpawn, fallbackSpawn) {
  return copySpawn(explicitSpawn) || getStageEntrySpawn(playerId, stage, fallbackSpawn);
}

export function prepareConnectedTransition(playerId, source, direction, cell) {
  const transition = getExitTransition(source, direction, cell);
  if (!transition || transition.destination === "world-map") return transition;
  prepareStageEntry(playerId, transition.destination, transition.spawn);
  return transition;
}
